/**
 * `throwProjection` — projects a {@link DiceThrowRequest} onto what `<DiceOverlay>` needs (the
 * dice specs to throw + the readout summary text), and folds in the **dice-count visualization
 * cap** so that decision is one pure, headlessly-testable seam rather than logic buried inside
 * the React component.
 *
 * The chat/resolution feed stays authoritative either way — `summary` is always populated for a
 * countable throw regardless of the cap; only the 3D tumble (`specs` actually reaching
 * `DiceStage.throwSpecs`) is withheld when the projected count exceeds
 * {@link MAX_VISUALIZED_DICE}. The overlay uses `visualize` to decide whether to mount/add to
 * the canvas at all — an oversized throw never spins up WebGL, it just shows the number.
 */
import type { DiceThrowRequest } from "./diceController";
import { diceSpecsFromRoll, formatRollSummary, type DiceSpec } from "./rollChoreography";
import { diceSpecsFromSteps, formatStepsSummary } from "./resolutionDice";
import { diceSpecsFromFeedRoll, formatFeedRollSummary } from "./feedRollDice";

/**
 * The most dice a single throw will get the 3D tumble for. Above this, the overlay skips the
 * canvas entirely and shows only the chat-feed-authoritative readout number.
 *
 * Lancer's largest RAW damage pools top out around 10-12 dice (a crit'd multi-die weapon plus
 * accuracy); an ad-hoc chat `/roll` is unbounded (`/roll 40d6` parses fine server-side). 30 gives
 * real combat pools generous headroom while still catching a genuinely oversized ad-hoc throw
 * before it (a) tumbles dozens of overlapping, hard-to-read dice, (b) lingers on screen far
 * longer than the number is worth watching, and (c) makes `recordThrowBatch`'s synchronous
 * per-die headless physics pre-roll expensive on the main thread.
 */
export const MAX_VISUALIZED_DICE = 30;

/** Whether a throw's projected dice count gets the 3D tumble (true) or just the readout (false). */
export function shouldVisualize(diceCount: number): boolean {
  return diceCount <= MAX_VISUALIZED_DICE;
}

/** A throw projected onto what the overlay needs, with the visualization-cap decision folded in. */
export type ThrowProjection = {
  /** `null` only for the dev physics-decides `d20` request, which has no recorded draws to project. */
  specs: DiceSpec[] | null;
  /** `null` only for the dev `d20` request — it has no readout panel. */
  summary: string | null;
  /** Whether `specs.length` clears {@link MAX_VISUALIZED_DICE} — the overlay's mount/skip decision. Always `true` for `d20`. */
  visualize: boolean;
};

/**
 * Project a throw request onto its dice specs + summary text, folding in the
 * visualization cap. Pure — the counting always uses the **actual projected dice** (one entry
 * per recorded draw/face — the same count `DiceStage.throwSpecs` would receive), never the pool
 * notation (`descriptor.count`/`NdM`), so an accuracy-widened or multi-step pool is counted
 * honestly even when its notation undercounts it.
 */
export function projectThrow(req: DiceThrowRequest): ThrowProjection {
  if (req.kind === "d20") return { specs: null, summary: null, visualize: true };
  const { specs, summary } =
    req.kind === "roll"
      ? { specs: diceSpecsFromRoll(req.result), summary: formatRollSummary(req.result) }
      : req.kind === "feedRoll"
        ? { specs: diceSpecsFromFeedRoll(req.entry), summary: formatFeedRollSummary(req.entry) }
        : { specs: diceSpecsFromSteps(req.steps), summary: formatStepsSummary(req.steps) };
  return { specs, summary, visualize: shouldVisualize(specs.length) };
}
